<script>
  import { onMount } from "svelte";
  import hotspotLogo from "../assets/img/hotspot.png";
  import { getHotspotData, loadingStart, loadingStop } from "./utils.svelte.js";
  import { getStore } from "./store.svelte.js";
  import Error from "./components/Error.svelte";
  import { captureException } from "@sentry/svelte";

  const baseURL = import.meta.env.VITE_BASE_URL;
  const store = getStore();

  // State
  let phoneNumber = null;
  let newAccount = false;

  let errorMessage = null;

  function clearInput() {
    document.getElementById("phoneNumber").value = "";
    document.getElementById("phoneNumber").focus();
  }

  function urlAccountRedirect() {
    // Redirect to the account view if the URL ends in account/
    const url = window.location.href;
    if (url.endsWith("account/")) {
      store.setState("ACCOUNT");
    }
  }

  // Check if the if phone number and MAC address combination exists
  // If true: log the user in
  async function macLogin() {
    if (store.macAddress === null) {
      captureException("store.macAddress null when attempting MAC login");
      errorMessage = "Device based login failed, please try again";
      return;
    }
    loadingStart();
    try {
      const response = await fetch(`${baseURL}mac/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: phoneNumber,
          mac: store.macAddress,
        }),
      });

      if (response.status === 200) {
        store.setState("ACCOUNT");
        return;
      } else if (response.status === 401) {
        return false;
      }
    } catch (error) {
      console.error("Error checking MAC status:", error);
    } finally {
      loadingStop();
    }
  }

  async function createAccount() {
    loadingStart();
    try {
      const response = await fetch(`${baseURL}create-account/pin/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          mac: store.macAddress,
        }),
      });

      if (response.status === 206) {
        // Remove this after 30 days
        store.setState("ACCOUNT");
      } else if (response.status === 200) {
        newAccount = true;
      } else if (response.status === 204) {
        newAccount = false;
      } else if (response.status === 403) {
        errorMessage = "Phone number is an invalid length";
        clearInput();
      } else {
        console.error("Failed to create account");
      }
    } catch (error) {
      console.error("Error creating account:", error);
    } finally {
      loadingStop();
    }
  }

  function validatePhoneNumber(phoneNumber) {
    if (phoneNumber === "" || phoneNumber === null) {
      errorMessage = "Invalid phone number";
    }

    let phoneNumberCleaned = phoneNumber.replace(/\s/g, "");

    if (phoneNumberCleaned.length !== 10) {
      errorMessage = "Phone number must be 10 characters long";
      return false;
    }

    // Check if the phone number starts with 0
    if (phoneNumberCleaned[0] !== "0") {
      errorMessage = "Phone number must start with 0";
      return false;
    }

    // Check if all characters in the phone number are numeric
    for (let i = 0; i < phoneNumberCleaned.length; i++) {
      const char = phoneNumberCleaned[i];
      if (isNaN(parseInt(char, 10))) {
        errorMessage = "Phone number can only contain numbers";
        return false;
      }
    }

    errorMessage = "";
  }

  // Form submission logic
  const handleSubmit = (event) => {
    if (validatePhoneNumber(phoneNumber) === false) {
      return;
    }

    event.preventDefault();

    store.setCellphone(phoneNumber); // Update the store state here

    macLogin();

    // If account exists, go to PIN login
    // If account does not exist, send PIN via SMS to the user and go straight to the app
    createAccount();
    if (newAccount) {
      store.setState("ACCOUNT");
      return;
    } else if (newAccount === false) {
      store.setState("PIN_LOGIN");
    }
  };

  onMount(() => {
    urlAccountRedirect();
    getHotspotData();

    document.getElementById("phoneNumber").focus();
  });
</script>

<div class="h-screen flex flex-col items-center py-6">
  <div class="flex justify-center mt-4 w-full mb-4">
    <img src={hotspotLogo} alt="TooMuchWifi Hotspot" class="w-40" />
  </div>

  <div class="my-8"></div>

  {#if errorMessage}
    <div class="w-80">
      <Error message={errorMessage}></Error>
      <div class="my-8"></div>
    </div>
  {/if}

  <div class="mx-10 my-6">
    <p class="font-medium text-center text-sm">
      Enter your phone number to get started
    </p>
  </div>

  <form on:submit={handleSubmit}>
    <label for="phoneNumber" class="sr-only">Phone number</label>
    <input
      type="tel"
      id="phoneNumber"
      placeholder="Phone Number"
      maxlength="10"
      bind:value={phoneNumber}
      required
      class="border border-gray-300 px-2 py-5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
    />
    <div class="h-8"></div>
    <button
      type="submit"
      class="bg-[#009946] py-5 text-white font-bold rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-80"
    >
      Connect
    </button>
  </form>

  <div class="mb-2 mt-auto w-full">
    <p class="text-center text-xs md:text-sm text-gray-500 w-full">
      By connecting, you agree to our
      <a
        href="https://toomuchwifi.co.za/terms-of-use"
        class="text-blue-500 hover:text-blue-700 underline"
      >
        Terms&nbsp;of&nbsp;Use
      </a>
      and
      <a
        href="https://toomuchwifi.co.za/privacy-policy"
        class="text-blue-500 hover:text-blue-700 underline"
      >
        Privacy&nbsp;Policy
      </a>.
    </p>
  </div>
</div>
